import React, { useMemo } from 'react';
import { TextInput, Checkbox, Button, useToast } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { FormFields } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import { getKycValidationSchema } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/utils';
import {
  FormHeading,
  FormFieldsWrapper,
  StyledForm,
  FormFooterWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { useMutation } from 'react-query';
import { VERIFY_PAN } from 'api/employeeOnboarding';
import api from 'api';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { useUserId } from 'components/EmployeeDetails/BankAccountDetails/hooks/useUserId';
import { useSavedEmployeeData } from './hooks/useSavedEmployeeData';

export const KycInfo: React.FC = () => {
  const {
    employeeDetails: { basicDetails },
  } = useSavedEmployeeData();
  const history = useHistory();

  const toast = useToast();
  const userId = useUserId();

  const {
    register,
    validate,
    formData,
    setIsSubmitting,
    isSubmitting,
    setFieldError,
    setFieldValue,
    errors,
  } = useFormWithProvider<FormFields>();

  const isPanVerified = formData.panVerificationStatus;

  const mutatePanVerification = useMutation({
    mutationKey: [VERIFY_PAN, userId],
    mutationFn: api.employeeOnboarding.verifyPan,
    onSuccess: (response) => {
      if (response.data.validation_status === 'valid') {
        setFieldValue('panVerificationStatus', true);
      } else {
        setFieldError('pan', response.data.error);
        toast.show({
          content: 'Invalid PAN details',
          color: 'negative',
        });
      }
      setIsSubmitting(false);
    },
    onError: (error: typeof AppError) => {
      setFieldError('pan', parseGraphqlErrorFromExtensions(error));
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
      setIsSubmitting(false);
    },
  });

  const handlePanVerification = async () => {
    if (!validate(getKycValidationSchema())) {
      return;
    }
    setIsSubmitting(true);
    mutatePanVerification.mutate({
      pan: formData.pan!,
      name: basicDetails?.name!,
      employee_type: 'employee',
    });
  };

  const handleConfirm = () => {
    history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.bankDetails);
  };

  const isFormValid = useMemo(
    () => validate(getKycValidationSchema(), { sideEffects: false }),
    [validate],
  );

  const canSubmitForm = isFormValid && isPanVerified;

  return (
    <StyledForm>
      <FormFieldsWrapper>
        <FormHeading
          title="KYC Information"
          description="We need to verify your identity and ensure secure account management."
        />
        <BorderedBox>
          <TextInput
            {...register('pan')}
            label="PAN"
            placeholder="ABCPE1234F"
            size="medium"
            necessityIndicator="required"
            isRequired
            isDisabled={isPanVerified}
            helpText="We need your PAN number to calculate your income tax."
            validationState={errors.pan ? 'error' : isPanVerified ? 'success' : 'none'}
            successText={isPanVerified ? 'PAN Verified' : undefined}
            autoFocus
          />

          <TextInput
            {...register('aadharNumber')}
            label="Aadhar Number"
            placeholder="1234 5678 9012"
            size="medium"
            necessityIndicator="required"
            isRequired
          />

          <Checkbox
            {...register('verificationConsent', 'checkbox')}
            isRequired
            isDisabled={isPanVerified}
            isChecked={formData.verificationConsent}>
            I consent to the validation of my details
          </Checkbox>
        </BorderedBox>
      </FormFieldsWrapper>

      <FormFooterWrapper>
        <Button
          variant="tertiary"
          size="medium"
          onClick={() =>
            history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.previewInformation)
          }>
          Back
        </Button>
        {isPanVerified ? (
          <Button
            variant="primary"
            size="medium"
            onClick={handleConfirm}
            isDisabled={!canSubmitForm}>
            Confirm
          </Button>
        ) : (
          <Button
            variant="primary"
            size="medium"
            onClick={handlePanVerification}
            isDisabled={!formData.verificationConsent}
            isLoading={isSubmitting}>
            Continue
          </Button>
        )}
      </FormFooterWrapper>
    </StyledForm>
  );
};
